import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { CSSProperties } from "preact/compat";

export const EditableText = ({
  value,
  onSave,
  className,
  inputClassName,
  placeholder = "",
  maxLength = 40,
  style,
  inputStyle,
  children,
}: {
  value: string;
  onSave: (newValue: string) => Promise<void> | void;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  maxLength?: number;
  style?: CSSProperties;
  inputStyle?: CSSProperties;
  children?: preact.ComponentChildren;
}) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const saveTriggeredRef = useRef(false);

  useEffect(() => {
    if (!editing) return;
    saveTriggeredRef.current = false;
    const clickHandler = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        handleSave();
      }
    };
    document.addEventListener("mousedown", clickHandler);
    return () => document.removeEventListener("mousedown", clickHandler);
  }, [editing, editValue]);

  useEffect(() => {
    if (editing) {
      setEditValue(value);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, [editing, value]);

  const handleSave = useCallback(async () => {
    if (saveTriggeredRef.current) return;
    saveTriggeredRef.current = true;
    setEditing(false);
    if (editValue !== value) {
      await onSave(editValue);
    }
  }, [editValue, value, onSave]);

  const handleAbort = useCallback(() => {
    saveTriggeredRef.current = true;
    setEditing(false);
    setEditValue(value);
  }, [value]);

  return (
    <div ref={wrapperRef} className={className} style={style}>
      {editing
        ? (
          <input
            ref={inputRef}
            className={inputClassName}
            value={editValue}
            autoFocus
            onChange={(e) => setEditValue((e.target as HTMLInputElement).value)}
            onBlur={handleSave}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") handleAbort();
            }}
            maxLength={maxLength}
            style={inputStyle}
            placeholder={placeholder}
          />
        )
        : (
          <span
            tabIndex={0}
            onClick={() => setEditing(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setEditing(true);
            }}
            style={value
              ? undefined
              : { color: "#9ca3af", fontStyle: "italic" }}
          >
            {children || value || placeholder}
          </span>
        )}
    </div>
  );
};
